import {
  Component,
  Input,
  ViewChild,
  ElementRef,
  Inject,
  PLATFORM_ID,
  ChangeDetectorRef,
  NgZone,
  OnDestroy,
  OnInit
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { isPlatformBrowser } from '@angular/common';
import { validationMessage } from '@constants/constant';
import { CommonService } from '@modules/shared/services/common.service';
import { Application } from '@enums/application.enums';
import { UploadService } from '@services/upload.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Field } from '@modules/dynamic-form-builder/model/formBuilderInterface';
import {
  CommonEnums,
  MULTIPLE_IMAGE_UPLOAD_DRAG,
  MULTIPLE_IMAGE_UPLOAD_STRINGS
} from '@modules/dynamic-form-builder/constants/constant';

const IMAGE_FILE_TYPES_MSG =
  'Any JPG, JPEG, PNG, HEIC, HEIF, WEBP, AVIF';
const DEFAULT_MAX_FILE_SIZE = Application.IMAGE_FILE_SIZE_LIMIT;
const DEFAULT_MIN_WIDTH = 456;
const DEFAULT_MIN_HEIGHT = 381;

/** Preview item: either a new File (with object URL) or an existing URL string (no file). */
export interface FilePreview {
  file?: File;
  previewUrl: string;
}

@Component({
  selector: 'app-multiple-image-upload',
  templateUrl: './multiple-image-upload.component.html',
  styleUrls: ['./multiple-image-upload.component.scss']
})
export class MultipleImageUploadComponent implements OnInit, OnDestroy {
  @Input() field: Field;
  @Input() form: FormGroup;
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  readonly IMAGE_FILE_TYPES_MSG = IMAGE_FILE_TYPES_MSG;
  application = Application;
  readonly CommonEnums = CommonEnums;
  readonly STRINGS = MULTIPLE_IMAGE_UPLOAD_STRINGS;
  readonly DRAG = MULTIPLE_IMAGE_UPLOAD_DRAG;
  previews: FilePreview[] = [];
  private objectUrls: string[] = [];

  constructor(
    private readonly cs: CommonService,
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    private readonly uploadService: UploadService,
    private readonly cdr: ChangeDetectorRef,
    private readonly ngZone: NgZone,
    private readonly sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.initPreviewsFromFormValue();
  }

  /** Sync previews from form control value (e.g. existing URLs from API). Only runs when previews are empty. */
  private initPreviewsFromFormValue(): void {
    if (!this.form || !this.field?.name || this.previews?.length) return;
    const control = this.form.get(this.field.name);
    if (!control) return;
    const val = control.value;
    if (!Array.isArray(val) || !val?.length) return;

    const list: FilePreview[] = [];
    for (const item of val) {
      if (typeof item === 'string') {
        list.push({ previewUrl: item });
      } else if (item instanceof File) {
        const previewUrl = URL.createObjectURL(item);
        this.objectUrls.push(previewUrl);
        list.push({ file: item, previewUrl });
      }
    }
    this.previews = list;
    this.cdr.markForCheck();
  }

  getSafePreviewUrl(url: string): SafeUrl {
    return url ? this.sanitizer.bypassSecurityTrustUrl(url) : '';
  }

  get maxFileSize(): number {
    return this.field?.fileSizeLimit ?? DEFAULT_MAX_FILE_SIZE;
  }

  get minWidth(): number {
    return this.field?.minWidth ?? this.field?.cropperOptions?.cropperMinWidth ?? DEFAULT_MIN_WIDTH;
  }

  get minHeight(): number {
    return this.field?.minHeight ?? this.field?.cropperOptions?.cropperMinHeight ?? DEFAULT_MIN_HEIGHT;
  }

  get recommendedSizeMessage(): string {
    return `Recommended size: ${this.minWidth} x ${this.minHeight} px`;
  }

  /** Form value: File for new uploads, string for existing URLs. */
  private getFormValue(): (File | string)[] {
    return this.previews.map((p) => (p.file != null ? p.file : p.previewUrl));
  }

  triggerFileInput(): void {
    this.fileInput?.nativeElement?.click();
  }

  fileChangeEvent(event: Event): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files?.length) return;

    let processed: number = Application.ZERO;
    const toAdd: FilePreview[] = [];
    const total = files.length;

    const tryFinish = (): void => {
      processed++;
      if (processed === total) {
        if (toAdd.length) {
          this.previews = [...this.previews, ...toAdd];
          this.patchForm();
          this.ngZone.run(() => this.cdr.detectChanges());
        }
        input.value = '';
      }
    };

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.size > this.maxFileSize) {
        this.cs.showErrorMessage(validationMessage.imageMax5);
        tryFinish();
        continue;
      }

      if (file.type === Application.IMAGE_GIF_TYPE) {
        this.validateAndAddFile(file, toAdd, tryFinish, true);
        continue;
      }

      this.uploadService
        .checkAndConvertHeicOrAvifToJpeg(file)
        .then((preparedFile) => {
          this.validateAndAddFile(preparedFile, toAdd, tryFinish, false);
        })
        .catch(() => {
          this.validateAndAddFile(file, toAdd, tryFinish, false);
        });
    }
  }

  private validateAndAddFile(
    file: File,
    toAdd: FilePreview[],
    tryFinish: () => void,
    _isGif: boolean
  ): void {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (e: ProgressEvent<FileReader>) => {
      const src = (e.target?.result as string) || '';
      const image = new Image();
      image.src = src;
      image.onload = () => {
        const w = image.naturalWidth;
        const h = image.naturalHeight;
        if (w >= this.minWidth && h >= this.minHeight) {
          const previewUrl = URL.createObjectURL(file);
          this.objectUrls.push(previewUrl);
          toAdd.push({ file, previewUrl });
        } else {
          this.cs.showErrorMessage(
            `Image dimensions must be at least ${this.minWidth}x${this.minHeight} pixels. Current: ${w}x${h} pixels.`
          );
        }
        tryFinish();
      };
      image.onerror = () => {
        tryFinish();
      };
    };
    reader.onerror = () => tryFinish();
  }

  removeAt(index: number): void {
    const item = this.previews[index];
    if (item?.file && item?.previewUrl) {
      URL.revokeObjectURL(item.previewUrl);
      this.objectUrls = this.objectUrls.filter((u) => u !== item.previewUrl);
    }
    this.previews.splice(index, 1);
    this.patchForm();
    this.cdr.detectChanges();
  }

  onPreviewDrop(event: CdkDragDrop<number>): void {
    const fromIndex = event.item.data as number;
    const toIndex = Number(event.container.data);
    if (fromIndex === toIndex) {
      return;
    }
    moveItemInArray(this.previews, fromIndex, toIndex);
    this.patchForm();
    this.cdr.detectChanges();
  }

  trackByPreview(_index: number, item: FilePreview): string {
    return item.file?.name ?? item.previewUrl;
  }

  private patchForm(): void {
    this.form?.patchValue({
      [this.field.name]: this.getFormValue()
    });
    this.form?.get(this.field.name)?.updateValueAndValidity();
  }

  ngOnDestroy(): void {
    this.objectUrls.forEach((url) => URL.revokeObjectURL(url));
    this.objectUrls = [];
  }
}
